import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeftIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  EnvelopeIcon,
  PhoneIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  ClockIcon,
  UserIcon,
  PlusIcon,
  ArrowRightIcon,
  XMarkIcon,
  EyeIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { DateInput } from '@/components/ui/DateInput';
import { toDateValue } from '@/utils/datetime';
import {
  vendorService,
  Vendor,
  VendorContact,
  Contract,
  VendorDocument,
  VendorStatusHistory,
  AuditLogEntry,
} from '../services/vendor.service';

type TabKey = 'overview' | 'contacts' | 'contracts' | 'documents' | 'history';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'contracts', label: 'Contracts' },
  { key: 'documents', label: 'Documents' },
  { key: 'history', label: 'History' },
];

const STATUS_BADGE_COLORS: Record<string, string> = {
  success: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
  warning: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
  danger: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
  secondary: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
};

const RISK_BADGE_COLORS: Record<string, string> = {
  success: 'bg-success-100 text-success-800 border-success-200 dark:bg-success-900/20 dark:text-success-300 dark:border-success-800',
  warning: 'bg-warning-100 text-warning-800 border-warning-200 dark:bg-warning-900/20 dark:text-warning-300 dark:border-warning-800',
  danger: 'bg-danger-100 text-danger-800 border-danger-200 dark:bg-danger-900/20 dark:text-danger-300 dark:border-danger-800',
  secondary: 'bg-secondary-100 text-secondary-800 border-secondary-200 dark:bg-secondary-900/20 dark:text-secondary-300 dark:border-secondary-800',
};

const CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: 'easeOut' },
  },
};

function formatAddress(vendor: Vendor): string {
  const parts = [
    vendor.addressStreet,
    vendor.addressCity,
    vendor.addressState,
    vendor.addressPostalCode,
    vendor.addressCountry,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '-';
}

export default function VendorDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  // Tab data
  const [contacts, setContacts] = useState<VendorContact[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [documents, setDocuments] = useState<VendorDocument[]>([]);
  const [history, setHistory] = useState<VendorStatusHistory[]>([]);
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [tabLoading, setTabLoading] = useState(false);

  const fetchVendor = useCallback(async () => {
    if (!id) {return;}
    try {
      setLoading(true);
      const data = await vendorService.getVendor(id);
      setVendor(data);
      // Load contacts if they came with the vendor
      if (data.contacts) {
        setContacts(data.contacts);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to fetch vendor:', err);
      setError('Failed to load vendor details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchVendor();
  }, [fetchVendor]);

  // Fetch tab-specific data when tab changes
  const fetchTabData = useCallback(async (tab: TabKey) => {
    if (!id) {return;}
    setTabLoading(true);
    try {
      switch (tab) {
        case 'contacts': {
          const contactsData = await vendorService.getVendorContacts(id);
          setContacts(contactsData);
          break;
        }
        case 'contracts': {
          const { contracts: contractsData } = await vendorService.getVendorContracts(id);
          setContracts(contractsData);
          break;
        }
        case 'documents': {
          const docsData = await vendorService.getVendorDocuments(id);
          setDocuments(docsData);
          break;
        }
        case 'history': {
          const [historyData, auditData] = await Promise.all([
            vendorService.getVendorHistory(id),
            vendorService.getVendorAuditLog(id),
          ]);
          setHistory(historyData);
          setAuditLog(auditData.entries);
          break;
        }
      }
    } catch (err) {
      console.error(`Failed to fetch ${tab} data:`, err);
    } finally {
      setTabLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (activeTab !== 'overview') {
      fetchTabData(activeTab);
    }
  }, [activeTab, fetchTabData]);

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <div className="text-lg text-secondary-600 dark:text-secondary-400">
              Loading vendor details...
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="p-4 sm:p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-danger-400 dark:text-danger-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
              Error Loading Vendor
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400 mb-4">
              {error ?? 'Vendor not found'}
            </p>
            <Button onClick={() => navigate('/legal/vendors')} variant="outline">
              Back to Vendors
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusConfig = vendorService.getVendorStatusConfig(vendor.status);
  const riskConfig = vendorService.getRiskTierConfig(vendor.riskTier);

  return (
    <motion.div
      variants={CONTAINER_VARIANTS}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/legal/vendors')}
              className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
            </button>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
                  {vendor.companyLegalName}
                </h1>
                <span className="text-sm font-mono text-secondary-500 dark:text-secondary-400">
                  {vendor.vendorNumber}
                </span>
              </div>
              <div className="flex items-center space-x-2 mt-1">
                <span className={cn('px-3 py-1 text-xs font-medium rounded-full', STATUS_BADGE_COLORS[statusConfig.color] ?? STATUS_BADGE_COLORS.secondary)}>
                  {statusConfig.label}
                </span>
                <span className={cn('px-3 py-1 text-xs font-medium rounded-full border', RISK_BADGE_COLORS[riskConfig.color] ?? RISK_BADGE_COLORS.secondary)}>
                  {riskConfig.label} Risk
                </span>
              </div>
            </div>
          </div>
          <Link to={`/legal/vendors/${vendor.id}/edit`}>
            <Button variant="outline">
              <PencilIcon className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* Tab Navigation */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="border-b border-secondary-200 dark:border-secondary-700">
          <nav className="flex space-x-8 -mb-px" aria-label="Tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'py-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                  activeTab === tab.key
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300 dark:text-secondary-400 dark:hover:text-secondary-300 dark:hover:border-secondary-600'
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </motion.div>

      {/* Tab Content */}
      <motion.div variants={ITEM_VARIANTS}>
        {activeTab === 'overview' && <OverviewTab vendor={vendor} />}
        {activeTab === 'contacts' && (
          <ContactsTab
            contacts={contacts}
            vendorId={vendor.id}
            loading={tabLoading}
            onContactAdded={(contact) => setContacts((prev) => [...prev, contact])}
            onContactUpdated={(updated) => setContacts((prev) => prev.map((c) => c.id === updated.id ? updated : c))}
          />
        )}
        {activeTab === 'contracts' && <ContractsTab contracts={contracts} vendorId={vendor.id} loading={tabLoading} />}
        {activeTab === 'documents' && <DocumentsTab documents={documents} vendorId={vendor.id} loading={tabLoading} onDocumentAdded={(doc) => setDocuments((prev) => [...prev, doc])} onDocumentUpdated={(doc) => setDocuments((prev) => prev.map((d) => d.id === doc.id ? doc : d))} onDocumentDeleted={(id) => setDocuments((prev) => prev.filter((d) => d.id !== id))} />}
        {activeTab === 'history' && <HistoryTab history={history} auditLog={auditLog} loading={tabLoading} />}
      </motion.div>
    </motion.div>
  );
}

// ============ Overview Tab ============

function OverviewTab({ vendor }: { vendor: Vendor }) {
  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
            Vendor Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <DetailField label="Company Name" value={vendor.companyLegalName} />
            <DetailField label="DBA" value={vendor.dbaName ?? '-'} />
            <DetailField label="EIN" value={vendor.ein ?? '-'} />
            <DetailField label="Address" value={formatAddress(vendor)} />
            <DetailField
              label="Phone"
              value={vendor.phone ?? '-'}
              icon={<PhoneIcon className="w-4 h-4 text-secondary-400" />}
            />
            <DetailField
              label="Website"
              value={vendor.website ?? '-'}
              icon={<GlobeAltIcon className="w-4 h-4 text-secondary-400" />}
              href={vendor.website ?? undefined}
            />
            <DetailField
              label="Type"
              value={vendorService.getVendorTypeLabel(vendor.vendorType)}
              icon={<BuildingOfficeIcon className="w-4 h-4 text-secondary-400" />}
            />
            <DetailField label="Industry" value={vendor.industryCode ?? '-'} />
            <DetailField label="Risk Tier" value={vendorService.getRiskTierConfig(vendor.riskTier).label} />
            <DetailField label="Status" value={vendorService.getVendorStatusConfig(vendor.status).label} />
            <DetailField label="Date Onboarded" value={vendorService.formatDate(vendor.dateOnboarded)} />
          </div>

          {/* Service Categories */}
          {vendor.serviceCategories && vendor.serviceCategories.length > 0 && (
            <div className="mt-6">
              <span className="text-sm font-medium text-secondary-500 dark:text-secondary-400 block mb-2">
                Service Categories
              </span>
              <div className="flex flex-wrap gap-2">
                {vendor.serviceCategories.map((cat) => (
                  <span
                    key={cat}
                    className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300 rounded-full"
                  >
                    {cat}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Departments Served */}
          {vendor.departmentsServed && vendor.departmentsServed.length > 0 && (
            <div className="mt-4">
              <span className="text-sm font-medium text-secondary-500 dark:text-secondary-400 block mb-2">
                Departments Served
              </span>
              <div className="flex flex-wrap gap-2">
                {vendor.departmentsServed.map((dept) => (
                  <span
                    key={dept}
                    className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300 rounded-full"
                  >
                    {dept}
                  </span>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      {vendor.notes && (
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-3">
              Notes
            </h3>
            <p className="text-secondary-700 dark:text-secondary-300 whitespace-pre-wrap">
              {vendor.notes}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DetailField({
  label,
  value,
  icon,
  href,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  href?: string;
}) {
  return (
    <div>
      <span className="text-sm font-medium text-secondary-500 dark:text-secondary-400 block mb-1">
        {label}
      </span>
      <div className="flex items-center space-x-2">
        {icon}
        {href ? (
          <a
            href={href.startsWith('http') ? href : `https://${href}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            {value}
          </a>
        ) : (
          <span className="text-sm text-secondary-900 dark:text-white">{value}</span>
        )}
      </div>
    </div>
  );
}

// ============ Contacts Tab ============

const CONTACT_ROLES = ['PRIMARY', 'BILLING', 'TECHNICAL', 'LEGAL', 'EXECUTIVE'] as const;

const ROLE_COLORS: Record<string, string> = {
  PRIMARY: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
  BILLING: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
  TECHNICAL: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
  LEGAL: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
  EXECUTIVE: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
};

const INITIAL_CONTACT_FORM = {
  fullName: '',
  jobTitle: '',
  email: '',
  phone: '',
  mobilePhone: '',
  role: 'PRIMARY',
  isPrimary: false,
  notes: '',
};

function ContactsTab({
  contacts,
  vendorId,
  loading,
  onContactAdded,
  onContactUpdated,
}: {
  contacts: VendorContact[];
  vendorId: string;
  loading: boolean;
  onContactAdded: (contact: VendorContact) => void;
  onContactUpdated: (contact: VendorContact) => void;
}) {
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState<VendorContact | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(INITIAL_CONTACT_FORM);

  const openAddModal = () => {
    setEditingContact(null);
    setForm(INITIAL_CONTACT_FORM);
    setShowModal(true);
  };

  const openEditModal = (contact: VendorContact) => {
    setEditingContact(contact);
    setForm({
      fullName: contact.fullName,
      jobTitle: contact.jobTitle ?? '',
      email: contact.email,
      phone: contact.phone ?? '',
      mobilePhone: contact.mobilePhone ?? '',
      role: contact.role,
      isPrimary: contact.isPrimary,
      notes: contact.notes ?? '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingContact(null);
    setForm(INITIAL_CONTACT_FORM);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.email.trim()) {return;}

    try {
      setSubmitting(true);
      const payload: Record<string, unknown> = {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        role: form.role,
        isPrimary: form.isPrimary,
      };
      payload.jobTitle = form.jobTitle.trim() || null;
      payload.phone = form.phone.trim() || null;
      payload.mobilePhone = form.mobilePhone.trim() || null;
      payload.notes = form.notes.trim() || null;

      if (editingContact) {
        const updated = await vendorService.updateVendorContact(vendorId, editingContact.id, payload);
        onContactUpdated(updated);
        toast.success('Contact updated successfully');
      } else {
        const newContact = await vendorService.createVendorContact(vendorId, payload);
        onContactAdded(newContact);
        toast.success('Contact added successfully');
      }
      closeModal();
    } catch (err) {
      const message = err instanceof Error ? err.message : editingContact ? 'Failed to update contact' : 'Failed to add contact';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="text-secondary-600 dark:text-secondary-400">Loading contacts...</div>
        </CardContent>
      </Card>
    );
  }

  const isEditing = editingContact !== null;
  const inputClassName =
    'w-full rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 px-3 py-2 text-sm text-secondary-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500';
  const labelClassName = 'block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1';

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" onClick={openAddModal}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Contact
        </Button>
      </div>

      {contacts.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <UserIcon className="w-12 h-12 text-secondary-400 dark:text-secondary-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
              No contacts found
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400">
              No contacts have been added for this vendor yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contacts.map((contact) => (
            <Card key={contact.id}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-secondary-900 dark:text-white">
                      {contact.fullName}
                    </h4>
                    {contact.jobTitle && (
                      <p className="text-sm text-secondary-500 dark:text-secondary-400">
                        {contact.jobTitle}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openEditModal(contact)}
                      className="p-1 text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
                      title="Edit contact"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <span className={cn(
                      'px-2 py-0.5 text-xs font-medium rounded-full',
                      ROLE_COLORS[contact.role] ?? 'bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300'
                    )}>
                      {contact.role}
                    </span>
                    {contact.isPrimary && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300 rounded-full border border-primary-200 dark:border-primary-800">
                        Primary
                      </span>
                    )}
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <EnvelopeIcon className="w-4 h-4 text-secondary-400" />
                    <a
                      href={`mailto:${contact.email}`}
                      className="text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {contact.email}
                    </a>
                  </div>
                  {contact.phone && (
                    <div className="flex items-center space-x-2">
                      <PhoneIcon className="w-4 h-4 text-secondary-400" />
                      <span className="text-secondary-700 dark:text-secondary-300">{contact.phone}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Contact Modal */}
      {showModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={closeModal}
          />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
            <div
              className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-lg w-full pointer-events-auto max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 pb-4 border-b border-secondary-200 dark:border-secondary-700">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                  {isEditing ? 'Edit Contact' : 'Add Contact'}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-1 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label htmlFor="contact-fullName" className={labelClassName}>
                      Full Name <span className="text-danger-500">*</span>
                    </label>
                    <input
                      id="contact-fullName"
                      name="fullName"
                      value={form.fullName}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className={inputClassName}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className={labelClassName}>
                      Email <span className="text-danger-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className={inputClassName}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-jobTitle" className={labelClassName}>
                      Job Title
                    </label>
                    <input
                      id="contact-jobTitle"
                      name="jobTitle"
                      value={form.jobTitle}
                      onChange={handleChange}
                      placeholder="Account Manager"
                      className={inputClassName}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className={labelClassName}>
                      Phone
                    </label>
                    <input
                      id="contact-phone"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="(555) 555-5555"
                      className={inputClassName}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-mobilePhone" className={labelClassName}>
                      Mobile Phone
                    </label>
                    <input
                      id="contact-mobilePhone"
                      name="mobilePhone"
                      value={form.mobilePhone}
                      onChange={handleChange}
                      placeholder="(555) 555-5555"
                      className={inputClassName}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-role" className={labelClassName}>
                      Role
                    </label>
                    <select
                      id="contact-role"
                      name="role"
                      value={form.role}
                      onChange={handleChange}
                      className={inputClassName}
                    >
                      {CONTACT_ROLES.map((role) => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="contact-isPrimary"
                      name="isPrimary"
                      type="checkbox"
                      checked={form.isPrimary}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="contact-isPrimary" className="ml-2 text-sm text-secondary-700 dark:text-secondary-300">
                      Primary Contact
                    </label>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="contact-notes" className={labelClassName}>
                      Notes
                    </label>
                    <textarea
                      id="contact-notes"
                      name="notes"
                      value={form.notes}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Additional notes..."
                      className={inputClassName}
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                  <Button type="button" variant="outline" onClick={closeModal} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? (isEditing ? 'Saving...' : 'Adding...') : (isEditing ? 'Save Changes' : 'Add Contact')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============ Contracts Tab ============

function ContractsTab({
  contracts,
  vendorId,
  loading,
}: {
  contracts: Contract[];
  vendorId: string;
  loading: boolean;
}) {
  if (loading) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="text-secondary-600 dark:text-secondary-400">Loading contracts...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Link to={`/legal/contracts/create?vendorId=${vendorId}`}>
          <Button>
            <PlusIcon className="w-4 h-4 mr-2" />
            Add Contract
          </Button>
        </Link>
      </div>

      {contracts.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <DocumentTextIcon className="w-12 h-12 text-secondary-400 dark:text-secondary-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
              No contracts found
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400">
              No contracts have been created for this vendor yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {contracts.map((contract) => {
            const statusCfg = vendorService.getContractStatusConfig(contract.status);
            return (
              <Link key={contract.id} to={`/legal/contracts/${contract.id}`} className="block">
                <Card hover="default" padding="none" className="transition-all duration-200 cursor-pointer">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-1">
                          <h4 className="font-semibold text-secondary-900 dark:text-white truncate">
                            {contract.title}
                          </h4>
                          <span className="text-sm font-mono text-secondary-500 dark:text-secondary-400">
                            {contract.contractNumber}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="text-xs text-secondary-500 dark:text-secondary-400">
                            {vendorService.getContractTypeLabel(contract.contractType)}
                          </span>
                          <span className={cn(
                            'px-2 py-0.5 text-xs font-medium rounded-full',
                            STATUS_BADGE_COLORS[statusCfg.color] ?? STATUS_BADGE_COLORS.secondary
                          )}>
                            {statusCfg.label}
                          </span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-secondary-600 dark:text-secondary-400">
                          {contract.contractValue !== null && contract.contractValue !== undefined && (
                            <span>{vendorService.formatCurrency(contract.contractValue, contract.currency)}</span>
                          )}
                          {contract.expirationDate && (
                            <span>Expires: {vendorService.formatDate(contract.expirationDate)}</span>
                          )}
                        </div>
                      </div>
                      <ArrowRightIcon className="w-5 h-5 text-secondary-400 dark:text-secondary-500 flex-shrink-0 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============ Documents Tab ============

function DocumentsTab({ documents, vendorId, loading, onDocumentAdded, onDocumentUpdated, onDocumentDeleted }: { documents: VendorDocument[]; vendorId: string; loading: boolean; onDocumentAdded: (doc: VendorDocument) => void; onDocumentUpdated: (doc: VendorDocument) => void; onDocumentDeleted: (id: string) => void }) {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<VendorDocument | null>(null);
  const [editingDocument, setEditingDocument] = useState<VendorDocument | null>(null);
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null);
  const [documentVersions, setDocumentVersions] = useState<VendorDocument[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionFile, setVersionFile] = useState<File | null>(null);
  const [versionUploading, setVersionUploading] = useState(false);
  const [versionProgress, setVersionProgress] = useState(0);
  const versionFileInputRef = useRef<HTMLInputElement>(null);
  // Expiration date fields are DateInput components (typed entry); the forms
  // read them via a hidden input carrying the committed value
  const [uploadDocExpiration, setUploadDocExpiration] = useState('');
  const [editDocExpiration, setEditDocExpiration] = useState('');

  useEffect(() => {
    if (!showUploadModal) { setUploadDocExpiration(''); }
  }, [showUploadModal]);

  useEffect(() => {
    setEditDocExpiration(
      editingDocument?.expirationDate ? toDateValue(new Date(editingDocument.expirationDate)) : ''
    );
  }, [editingDocument]);

  const inputCls = 'w-full rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 px-3 py-2 text-sm text-secondary-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500';
  const labelCls = 'block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1';

  // Load versions when preview opens
  useEffect(() => {
    if (!previewDocument) { setDocumentVersions([]); return; }
    setVersionsLoading(true);
    vendorService.getDocumentVersions(previewDocument.id)
      .then(setDocumentVersions)
      .catch(() => setDocumentVersions([]))
      .finally(() => setVersionsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- previewDocument.id is the only relevant change
  }, [previewDocument?.id]);

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) {return `${bytes} B`;}
    if (bytes < 1024 * 1024) {return `${(bytes / 1024).toFixed(1)} KB`;}
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="text-secondary-600 dark:text-secondary-400">Loading documents...</div>
        </CardContent>
      </Card>
    );
  }

  const CATEGORY_COLORS: Record<string, string> = {
    CONTRACT_EXECUTED: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
    AMENDMENT: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
    INSURANCE_CERTIFICATE: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
    COMPLIANCE_CERTIFICATE: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
    NDA: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
    FINANCIAL_STATEMENT: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
    OTHER: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300',
  };

  const handleUpload = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedFile) { toast.error('Please select a file'); return; }
    const form = e.currentTarget;
    const data = new FormData(form);
    try {
      setUploading(true); setUploadProgress(0);
      const metadata: Record<string, string> = {
        displayName: (data.get('displayName') as string)?.trim() || selectedFile.name,
        category: data.get('category') as string || 'OTHER',
        confidentialityLevel: data.get('confidentialityLevel') as string || 'INTERNAL',
      };
      const desc = (data.get('description') as string)?.trim();
      if (desc) {metadata.description = desc;}
      const expDate = data.get('expirationDate') as string;
      if (expDate) {metadata.expirationDate = expDate;}
      const newDoc = await vendorService.uploadVendorDocument(vendorId, selectedFile, metadata, setUploadProgress);
      onDocumentAdded(newDoc);
      setShowUploadModal(false); setSelectedFile(null);
      toast.success('Document uploaded successfully');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload document');
    } finally { setUploading(false); setUploadProgress(0); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" onClick={() => setShowUploadModal(true)}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {documents.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <DocumentTextIcon className="w-12 h-12 text-secondary-400 dark:text-secondary-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">No documents found</h3>
            <p className="text-secondary-600 dark:text-secondary-400">No documents have been uploaded for this vendor yet.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-3">
              {documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-800/50">
                  <div className="flex items-center gap-3 min-w-0">
                    <DocumentTextIcon className="w-8 h-8 text-secondary-400 dark:text-secondary-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">{doc.displayName}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={cn('px-2 py-0.5 text-xs font-medium rounded-full', CATEGORY_COLORS[doc.category] ?? CATEGORY_COLORS.OTHER)}>
                          {doc.category.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-secondary-500 dark:text-secondary-400">v{doc.version}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                    <div className="text-right">
                      {doc.expirationDate && <p className="text-xs text-secondary-500 dark:text-secondary-400">Expires: {vendorService.formatDate(doc.expirationDate)}</p>}
                      <p className="text-xs text-secondary-400 dark:text-secondary-500">Uploaded: {vendorService.formatDate(doc.createdAt)}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setPreviewDocument(doc)} className="p-1 text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 rounded hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors" title="Preview">
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button onClick={() => setEditingDocument(doc)} className="p-1 text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 rounded hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors" title="Edit metadata">
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button onClick={() => setDeletingDocumentId(doc.id)} className="p-1 text-secondary-400 hover:text-danger-600 dark:hover:text-danger-400 rounded hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors" title="Delete">
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Document Modal */}
      {showUploadModal && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => { setShowUploadModal(false); setSelectedFile(null); }} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
            <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-lg w-full pointer-events-auto max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 pb-4 border-b border-secondary-200 dark:border-secondary-700">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Upload Document</h3>
                <button onClick={() => { setShowUploadModal(false); setSelectedFile(null); }} className="p-1 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"><XMarkIcon className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleUpload} className="p-6 space-y-4">
                <div>
                  <label className={labelCls}>File <span className="text-danger-500">*</span></label>
                  <input type="file" onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)} className="block w-full text-sm text-secondary-500 dark:text-secondary-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 dark:file:bg-primary-900/20 dark:file:text-primary-300 hover:file:bg-primary-100 dark:hover:file:bg-primary-900/30" />
                  {selectedFile && <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">{selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)</p>}
                </div>
                <div><label htmlFor="vdoc-displayName" className={labelCls}>Display Name</label><input id="vdoc-displayName" name="displayName" placeholder={selectedFile?.name ?? 'Document name'} className={inputCls} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label htmlFor="vdoc-category" className={labelCls}>Category</label>
                    <select id="vdoc-category" name="category" className={inputCls} defaultValue="OTHER">
                      <option value="CONTRACT_EXECUTED">Executed Contract</option><option value="AMENDMENT">Amendment</option><option value="INSURANCE_CERTIFICATE">Insurance Certificate</option><option value="SOC_REPORT">SOC Report</option><option value="W9_TAX_FORM">W-9 Tax Form</option><option value="FINANCIAL_STATEMENT">Financial Statement</option><option value="COMPLIANCE_CERTIFICATE">Compliance Certificate</option><option value="CORRESPONDENCE">Correspondence</option><option value="PROPOSAL">Proposal</option><option value="INVOICE">Invoice</option><option value="NDA">NDA</option><option value="BACKGROUND_CHECK">Background Check</option><option value="OTHER">Other</option>
                    </select></div>
                  <div><label htmlFor="vdoc-confidentiality" className={labelCls}>Confidentiality</label>
                    <select id="vdoc-confidentiality" name="confidentialityLevel" className={inputCls} defaultValue="INTERNAL">
                      <option value="PUBLIC">Public</option><option value="INTERNAL">Internal</option><option value="CONFIDENTIAL">Confidential</option><option value="HIGHLY_CONFIDENTIAL">Highly Confidential</option>
                    </select></div>
                </div>
                <div><DateInput id="vdoc-expirationDate" name="expirationDate" label="Expiration Date" value={uploadDocExpiration} onChange={setUploadDocExpiration} /></div>
                <div><label htmlFor="vdoc-description" className={labelCls}>Description</label><textarea id="vdoc-description" name="description" rows={2} placeholder="Optional description..." className={inputCls} /></div>
                {uploading && uploadProgress > 0 && <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-2"><div className="bg-primary-500 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} /></div>}
                <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                  <Button type="button" variant="outline" onClick={() => { setShowUploadModal(false); setSelectedFile(null); }} disabled={uploading}>Cancel</Button>
                  <Button type="submit" disabled={uploading || !selectedFile}>{uploading ? 'Uploading...' : 'Upload'}</Button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Edit Document Metadata Modal */}
      {editingDocument && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => setEditingDocument(null)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
            <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-lg w-full pointer-events-auto max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 pb-4 border-b border-secondary-200 dark:border-secondary-700">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Edit Document</h3>
                <button onClick={() => setEditingDocument(null)} className="p-1 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"><XMarkIcon className="w-5 h-5" /></button>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget; const data = new FormData(form);
                try {
                  setSubmitting(true);
                  const payload: Record<string, string | undefined> = { displayName: (data.get('displayName') as string)?.trim() || undefined, category: data.get('category') as string || undefined, description: (data.get('description') as string)?.trim() || undefined, expirationDate: data.get('expirationDate') as string || undefined, confidentialityLevel: data.get('confidentialityLevel') as string || undefined };
                  const updated = await vendorService.updateDocument(editingDocument.id, payload);
                  onDocumentUpdated(updated);
                  if (previewDocument?.id === updated.id) {setPreviewDocument(updated);}
                  setEditingDocument(null);
                  toast.success('Document updated successfully');
                } catch (err) { toast.error(err instanceof Error ? err.message : 'Failed to update document'); } finally { setSubmitting(false); }
              }} className="p-6 space-y-4">
                <div><label htmlFor="edit-vdoc-displayName" className={labelCls}>Display Name</label><input id="edit-vdoc-displayName" name="displayName" defaultValue={editingDocument.displayName} className={inputCls} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label htmlFor="edit-vdoc-category" className={labelCls}>Category</label>
                    <select id="edit-vdoc-category" name="category" defaultValue={editingDocument.category} className={inputCls}>
                      <option value="CONTRACT_EXECUTED">Executed Contract</option><option value="AMENDMENT">Amendment</option><option value="INSURANCE_CERTIFICATE">Insurance Certificate</option><option value="SOC_REPORT">SOC Report</option><option value="W9_TAX_FORM">W-9 Tax Form</option><option value="FINANCIAL_STATEMENT">Financial Statement</option><option value="COMPLIANCE_CERTIFICATE">Compliance Certificate</option><option value="CORRESPONDENCE">Correspondence</option><option value="PROPOSAL">Proposal</option><option value="INVOICE">Invoice</option><option value="NDA">NDA</option><option value="BACKGROUND_CHECK">Background Check</option><option value="OTHER">Other</option>
                    </select></div>
                  <div><label htmlFor="edit-vdoc-confidentiality" className={labelCls}>Confidentiality</label>
                    <select id="edit-vdoc-confidentiality" name="confidentialityLevel" defaultValue={editingDocument.confidentialityLevel} className={inputCls}>
                      <option value="PUBLIC">Public</option><option value="INTERNAL">Internal</option><option value="CONFIDENTIAL">Confidential</option><option value="HIGHLY_CONFIDENTIAL">Highly Confidential</option>
                    </select></div>
                </div>
                <div><DateInput id="edit-vdoc-expirationDate" name="expirationDate" label="Expiration Date" value={editDocExpiration} onChange={setEditDocExpiration} /></div>
                <div><label htmlFor="edit-vdoc-description" className={labelCls}>Description</label><textarea id="edit-vdoc-description" name="description" rows={2} defaultValue={editingDocument.description ?? ''} className={inputCls} /></div>
                <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                  <Button type="button" variant="outline" onClick={() => setEditingDocument(null)} disabled={submitting}>Cancel</Button>
                  <Button type="submit" disabled={submitting}>{submitting ? 'Saving...' : 'Save Changes'}</Button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Delete Document Confirmation */}
      {deletingDocumentId && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => setDeletingDocumentId(null)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
            <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-sm w-full pointer-events-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">Delete Document</h3>
                <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-6">Delete this document? This cannot be undone.</p>
                <div className="flex justify-end space-x-3">
                  <Button variant="outline" onClick={() => setDeletingDocumentId(null)} disabled={submitting}>Cancel</Button>
                  <Button variant="destructive" disabled={submitting} onClick={async () => {
                    try {
                      setSubmitting(true);
                      await vendorService.deleteDocument(deletingDocumentId);
                      onDocumentDeleted(deletingDocumentId);
                      if (previewDocument?.id === deletingDocumentId) {setPreviewDocument(null);}
                      setDeletingDocumentId(null);
                      toast.success('Document deleted');
                    } catch (err) { toast.error(err instanceof Error ? err.message : 'Failed to delete document'); } finally { setSubmitting(false); }
                  }}>{submitting ? 'Deleting...' : 'Delete'}</Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Document Preview Drawer */}
      {previewDocument && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => { setPreviewDocument(null); setVersionFile(null); }} />
          <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white dark:bg-secondary-900 shadow-xl z-50 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-secondary-200 dark:border-secondary-700">
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white truncate">{previewDocument.displayName}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className={cn('px-2 py-0.5 text-xs font-medium rounded-full', CATEGORY_COLORS[previewDocument.category] ?? CATEGORY_COLORS.OTHER)}>{previewDocument.category.replace(/_/g, ' ')}</span>
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300">{previewDocument.confidentialityLevel.replace(/_/g, ' ')}</span>
                </div>
              </div>
              <button onClick={() => { setPreviewDocument(null); setVersionFile(null); }} className="p-1 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors ml-4"><XMarkIcon className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div>
                <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">Details</h4>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <dt className="text-secondary-500 dark:text-secondary-400">File Name</dt><dd className="text-secondary-900 dark:text-white truncate">{previewDocument.fileName}</dd>
                  <dt className="text-secondary-500 dark:text-secondary-400">Size</dt><dd className="text-secondary-900 dark:text-white">{formatFileSize(previewDocument.fileSize)}</dd>
                  <dt className="text-secondary-500 dark:text-secondary-400">Type</dt><dd className="text-secondary-900 dark:text-white">{previewDocument.mimeType}</dd>
                  <dt className="text-secondary-500 dark:text-secondary-400">Version</dt><dd className="text-secondary-900 dark:text-white">v{previewDocument.version}</dd>
                  <dt className="text-secondary-500 dark:text-secondary-400">Uploaded</dt><dd className="text-secondary-900 dark:text-white">{vendorService.formatDate(previewDocument.createdAt)}</dd>
                  {previewDocument.expirationDate && (<><dt className="text-secondary-500 dark:text-secondary-400">Expires</dt><dd className="text-secondary-900 dark:text-white">{vendorService.formatDate(previewDocument.expirationDate)}</dd></>)}
                </dl>
                {previewDocument.description && <p className="mt-3 text-sm text-secondary-600 dark:text-secondary-400">{previewDocument.description}</p>}
              </div>
              <Button className="w-full" onClick={async () => { try { await vendorService.downloadDocument(previewDocument.id, previewDocument.fileName); toast.success('Download started'); } catch (err) { toast.error(err instanceof Error ? err.message : 'Failed to download'); } }}>Download Document</Button>
              <div className="border-t border-secondary-200 dark:border-secondary-700 pt-4">
                <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">Upload New Version</h4>
                <div className="space-y-3">
                  <input ref={versionFileInputRef} type="file" onChange={(e) => setVersionFile(e.target.files?.[0] ?? null)} className="block w-full text-sm text-secondary-500 dark:text-secondary-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 dark:file:bg-primary-900/20 dark:file:text-primary-300 hover:file:bg-primary-100 dark:hover:file:bg-primary-900/30" />
                  {versionFile && (<>
                    <textarea id="vdoc-version-changeNotes" placeholder="Change notes (optional)..." rows={2} className={inputCls} />
                    {versionUploading && versionProgress > 0 && <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-2"><div className="bg-primary-500 h-2 rounded-full transition-all" style={{ width: `${versionProgress}%` }} /></div>}
                    <Button className="w-full" disabled={versionUploading} onClick={async () => {
                      try {
                        setVersionUploading(true); setVersionProgress(0);
                        const changeNotes = (document.getElementById('vdoc-version-changeNotes') as HTMLTextAreaElement)?.value?.trim() || undefined;
                        const newVersion = await vendorService.uploadDocumentVersion(previewDocument.id, versionFile, changeNotes, setVersionProgress);
                        onDocumentUpdated({ ...previewDocument, version: newVersion.version });
                        setDocumentVersions((prev) => [newVersion, ...prev]);
                        setVersionFile(null);
                        if (versionFileInputRef.current) {versionFileInputRef.current.value = '';}
                        toast.success(`Version ${newVersion.version} uploaded`);
                      } catch (err) { toast.error(err instanceof Error ? err.message : 'Failed to upload version'); } finally { setVersionUploading(false); setVersionProgress(0); }
                    }}>{versionUploading ? 'Uploading...' : 'Upload Version'}</Button>
                  </>)}
                </div>
              </div>
              <div className="border-t border-secondary-200 dark:border-secondary-700 pt-4">
                <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">Version History</h4>
                {versionsLoading ? <p className="text-sm text-secondary-500 dark:text-secondary-400">Loading versions...</p>
                  : documentVersions.length === 0 ? <p className="text-sm text-secondary-500 dark:text-secondary-400">No previous versions.</p>
                  : <div className="space-y-2">{documentVersions.map((ver) => (
                    <div key={ver.id} className="flex items-center justify-between p-2 rounded-lg border border-secondary-200 dark:border-secondary-700">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-secondary-900 dark:text-white">v{ver.version}</p>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">{vendorService.formatDate(ver.createdAt)}</p>
                        {ver.changeNotes && <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-0.5">{ver.changeNotes}</p>}
                      </div>
                      <Button variant="outline" size="sm" onClick={async () => { try { await vendorService.downloadDocument(ver.id, ver.fileName); } catch (err) { toast.error(err instanceof Error ? err.message : 'Failed to download'); } }}>Download</Button>
                    </div>
                  ))}</div>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============ History Tab ============

function HistoryTab({ history, auditLog, loading }: { history: VendorStatusHistory[]; auditLog: AuditLogEntry[]; loading: boolean }) {
  if (loading) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="text-secondary-600 dark:text-secondary-400">Loading history...</div>
        </CardContent>
      </Card>
    );
  }

  const ACTION_COLORS: Record<string, string> = {
    CREATED: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
    UPDATED: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
    DELETED: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
    STATUS_CHANGED: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
    UPLOADED: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
  };

  const RESOURCE_LABELS: Record<string, string> = {
    vendor: 'Vendor',
    vendor_contact: 'Contact',
    vendor_document: 'Document',
    contract: 'Contract',
    contract_amendment: 'Amendment',
    contract_obligation: 'Obligation',
    contact_interaction: 'Interaction',
  };

  function getActionColor(action: string): string {
    const key = action.split('_').slice(1).join('_');
    return ACTION_COLORS[key] ?? ACTION_COLORS.UPDATED;
  }

  function getActionLabel(action: string): string {
    return action.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
  }

  function parseChanges(oldValues?: string, newValues?: string): { field: string; from?: string; to: string }[] {
    const changes: { field: string; from?: string; to: string }[] = [];
    try {
      const newObj = newValues ? JSON.parse(newValues) : {};
      const oldObj = oldValues ? JSON.parse(oldValues) : {};
      const allKeys = new Set([...Object.keys(newObj), ...Object.keys(oldObj)]);
      for (const key of allKeys) {
        if (key === 'vendorId' || key === 'id' || key === 'contractId') {continue;}
        const newVal = newObj[key];
        const oldVal = oldObj[key];
        if (newVal !== undefined && oldVal !== undefined && String(newVal) !== String(oldVal)) {
          changes.push({ field: key, from: String(oldVal), to: String(newVal) });
        } else if (newVal !== undefined && oldVal === undefined) {
          changes.push({ field: key, to: String(newVal) });
        }
      }
    } catch {
      // Silently handle parse errors
    }
    return changes;
  }

  // Build combined timeline: status changes + audit log entries
  type TimelineEntry = { id: string; type: 'status' | 'audit'; date: string; statusEntry?: VendorStatusHistory; auditEntry?: AuditLogEntry };
  const timeline: TimelineEntry[] = [
    ...history.map((h) => ({ id: `status-${h.id}`, type: 'status' as const, date: h.changedAt, statusEntry: h })),
    ...auditLog.map((a) => ({ id: `audit-${a.id}`, type: 'audit' as const, date: a.createdAt, auditEntry: a })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  if (timeline.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <ClockIcon className="w-12 h-12 text-secondary-400 dark:text-secondary-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
            No history found
          </h3>
          <p className="text-secondary-600 dark:text-secondary-400">
            No changes have been recorded for this vendor yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {timeline.map((entry) => {
              if (entry.type === 'status' && entry.statusEntry) {
                const se = entry.statusEntry;
                const fromConfig = vendorService.getVendorStatusConfig(se.fromStatus);
                const toConfig = vendorService.getVendorStatusConfig(se.toStatus);
                return (
                  <div key={entry.id} className="flex gap-4 p-3 rounded-lg border border-secondary-200 dark:border-secondary-700">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-warning-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300">
                          Status changed
                        </span>
                        <span className={cn('px-2 py-0.5 text-xs font-medium rounded-full', STATUS_BADGE_COLORS[fromConfig.color] ?? STATUS_BADGE_COLORS.secondary)}>
                          {fromConfig.label}
                        </span>
                        <ArrowRightIcon className="w-3 h-3 text-secondary-400" />
                        <span className={cn('px-2 py-0.5 text-xs font-medium rounded-full', STATUS_BADGE_COLORS[toConfig.color] ?? STATUS_BADGE_COLORS.secondary)}>
                          {toConfig.label}
                        </span>
                        <span className="text-xs text-secondary-500 dark:text-secondary-400">
                          {vendorService.formatTimestamp(se.changedAt)}
                        </span>
                      </div>
                      {se.comments && (
                        <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">{se.comments}</p>
                      )}
                    </div>
                  </div>
                );
              }

              if (entry.type === 'audit' && entry.auditEntry) {
                const ae = entry.auditEntry;
                const changes = parseChanges(ae.oldValues, ae.newValues);
                return (
                  <div key={entry.id} className="flex gap-4 p-3 rounded-lg border border-secondary-200 dark:border-secondary-700">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-secondary-400 dark:bg-secondary-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={cn('px-2 py-0.5 text-xs font-medium rounded-full', getActionColor(ae.action))}>
                          {getActionLabel(ae.action)}
                        </span>
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300">
                          {RESOURCE_LABELS[ae.resourceType] ?? ae.resourceType}
                        </span>
                        <span className="text-xs text-secondary-500 dark:text-secondary-400">
                          {vendorService.formatTimestamp(ae.createdAt)}
                        </span>
                        {ae.userId && (
                          <span className="text-xs text-secondary-400 dark:text-secondary-500 font-mono">
                            {ae.userId.substring(0, 8)}...
                          </span>
                        )}
                      </div>
                      {changes.length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {changes.map((change, i) => (
                            <p key={i} className="text-xs text-secondary-600 dark:text-secondary-400">
                              <span className="font-medium">{change.field.replace(/([A-Z])/g, ' $1').trim()}</span>
                              {change.from ? (
                                <span>: <span className="line-through text-secondary-400">{change.from}</span> → {change.to}</span>
                              ) : (
                                <span>: {change.to}</span>
                              )}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              }

              return null;
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
